/**
 * What a 403 means to the person in front of it (H6).
 *
 * A 403 from this server is a refusal of one action, not a broken session: the
 * account is fine, the server just won't do this particular thing for this
 * person here. The app used to answer every one of them by blanking itself and
 * offering "Go to Login", so a community-scoped admin who pressed Save on a
 * setting they can't change was signed out, signed back in, and met the same
 * wall. None of these codes are TENANT_ACCESS_DENIAL_CODES, so NoAccessScreen
 * never covered them either.
 *
 * Each refusal says what was refused, why, and that the data is untouched. Only
 * NO_USER offers a sign-out, because another account is the one thing that
 * could actually help there.
 */
export interface AccessRefusal {
  title: string;
  message: string;
  /** Whether signing in as someone else could change the answer. */
  offerSignOut: boolean;
}

const UNCHANGED = "Nothing was changed.";

const REFUSALS: Record<string, AccessRefusal> = {
  UNRESTRICTED_ACCESS_REQUIRED: {
    title: "This needs access to the whole instance",
    message: `Your access is limited to some locations or departments, and this setting is shared by all of them. Someone who reaches every location can change it. ${UNCHANGED}`,
    offerSignOut: false,
  },
  INSUFFICIENT_PERMISSIONS: {
    title: "Your role can't do this",
    message: `This needs a permission your role doesn't have. An admin can change what your role may do. ${UNCHANGED}`,
    offerSignOut: false,
  },
  INSUFFICIENT_ROLE: {
    title: "Your role can't do this",
    message: `This is kept to other roles. An admin can change which role you hold. ${UNCHANGED}`,
    offerSignOut: false,
  },
  INSTANCE_REQUIRED: {
    title: "No instance chosen",
    message: `This needs an instance to act on. Choose one in the header, then try again. ${UNCHANGED}`,
    offerSignOut: false,
  },
  NO_USER: {
    title: "You have no account in this instance",
    message: `Ask an admin to add you, or sign in with the account that has access. ${UNCHANGED}`,
    offerSignOut: true,
  },
};

/** The refusal a 403's code stands for, or null when there is no code to read. */
export function refusalFor(code: string | undefined): AccessRefusal | null {
  if (!code) return null;
  return (
    REFUSALS[code] ?? {
      title: "You can't do this here",
      message: `The server refused it, and didn't say why. ${UNCHANGED}`,
      offerSignOut: false,
    }
  );
}
